import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Building2, FileText, CreditCard, File, Search, MapPin, Calendar, AlertCircle, X, User, Mail, MoreVertical, Eye, Download, Upload, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ChevronRight, ChevronLeft, Building, Home } from 'lucide-react';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatDateDisplay } from '../utils/dateUtils';

// UAE Emirates list
const UAE_EMIRATES = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ajman',
  'Ras Al Khaimah',
  'Fujairah',
  'Umm Al Quwain'
];

// Validation functions
const validateTRN = (trn) => {
  // UAE TRN is 15 digits
  const trnRegex = /^\d{15}$/;
  return trnRegex.test(trn.replace(/\s/g, ''));
};

const validateUAEMobile = (mobile) => {
  // UAE mobile: +971 5X XXX XXXX or 05X XXX XXXX
  const cleanMobile = mobile.replace(/[\s\-]/g, '');
  const mobileRegex = /^(\+971|00971|971)?0?5[0-9]\d{7}$/;
  return mobileRegex.test(cleanMobile);
};

const formatUAEMobile = (value) => {
  // Remove all non-digits except +
  let cleaned = value.replace(/[^\d+]/g, '');
  
  // If starts with 05, convert to +971 5
  if (cleaned.startsWith('05')) {
    cleaned = '+971' + cleaned.substring(1);
  }
  // If starts with 971, add +
  if (cleaned.startsWith('971') && !cleaned.startsWith('+')) {
    cleaned = '+' + cleaned;
  }
  
  // Format: +971 5X XXX XXXX
  if (cleaned.startsWith('+971') && cleaned.length > 4) {
    const rest = cleaned.substring(4);
    if (rest.length <= 2) return `+971 ${rest}`;
    if (rest.length <= 5) return `+971 ${rest.substring(0, 2)} ${rest.substring(2)}`;
    return `+971 ${rest.substring(0, 2)} ${rest.substring(2, 5)} ${rest.substring(5, 9)}`;
  }
  
  return cleaned;
};

import { usePermissions } from '../utils/permissionUtils';
import { formatErrorMessage } from '../utils/formatError';

const Lessees = ({ user }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks for module
  const permissions = usePermissions(user, 'lessees');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  
  // Document-specific permissions (using subPages)
  const canViewDocuments = permissions.canViewSubPage('documents');
  const canDownloadDocuments = permissions.canViewSubPage('downloadDocuments') || permissions.canViewSubPage('documents');
  const canReuploadDocuments = permissions.canEditSubPage('documents') || canEdit;
  
  const [lessees, setLessees] = useState([]);
  const [filteredLessees, setFilteredLessees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLessee, setSelectedLessee] = useState(null);
  
  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(0); // 0 = type selection, 1 = company, 2 = owner, 3 = documents, 4 = review
  const [lesseeType, setLesseeType] = useState(''); // 'in-house' or 'external'
  const [stepValidation, setStepValidation] = useState({
    company: false,
    owner: false,
    documents: false
  });
  
  // Validation error states
  const [validationErrors, setValidationErrors] = useState({
    trn_no: '',
    contact_mobile: ''
  });
  
  // Multiple trade licenses (up to 5)
  const [tradeLicenses, setTradeLicenses] = useState([
    { city: '', license_no: '', expiry: '', doc_url: '' }
  ]);
  
  // Lease contract
  const [leaseContract, setLeaseContract] = useState({
    doc_url: '',
    start_date: '',
    end_date: ''
  });
  
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    trn_no: '',
    owner_name: '',
    owner_emirates_id: '',
    owner_passport_no: '',
    // Contact Person fields
    contact_person_name: '',
    contact_email: '',
    contact_mobile: '',
    contact_designation: '',
    status: 'active',
    lessee_type: '',
    maintenance_contract: 'non_amc', // AMC or Non-AMC
    trn_doc_url: '',
    owner_emirates_id_doc_url: '',
    owner_passport_doc_url: ''
  });

  // Pending file uploads (actual File objects)
  const [pendingFiles, setPendingFiles] = useState({
    trn: null,
    owner_emirates_id: null,
    owner_passport: null,
    lease_contract: null,
    trade_licenses: [] // Array to store files for each trade license
  });
  const [uploadingDocs, setUploadingDocs] = useState(false);

  useEffect(() => {
    fetchLessees();
  }, []);

  useEffect(() => {
    // Apply search when searchQuery or lessees changes
    if (!searchQuery.trim()) {
      setFilteredLessees(lessees);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = lessees.filter(lessee =>
      lessee.company_name?.toLowerCase().includes(query) ||
      lessee.owner_name?.toLowerCase().includes(query) ||
      lessee.contact_person_name?.toLowerCase().includes(query) ||
      (lessee.contact_email || lessee.owner_email || '')?.toLowerCase().includes(query) ||
      (lessee.contact_mobile || lessee.owner_mobile || '')?.toLowerCase().includes(query) ||
      (lessee.trade_licenses || []).some(tl => tl.license_no?.toLowerCase().includes(query)) ||
      (lessee.trn_no || lessee.trn_number || '')?.toLowerCase().includes(query)
    );
    setFilteredLessees(filtered);
  }, [searchQuery, lessees]);

  const fetchLessees = async () => {
    try {
      const response = await axios.get(`${API}/lessees`, {
        headers: getAuthHeader()
      });
      setLessees(response.data);
      setFilteredLessees(response.data);
    } catch (error) {
      toast.error('Failed to fetch lessees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadingDocs(true);
    
    try {
      // Prepare data with trade licenses and lease contract
      // Don't include doc_url filenames - they'll be set after upload
      const submitData = {
        ...formData,
        trade_licenses: tradeLicenses.filter(tl => tl.city && tl.license_no).map(tl => ({
          ...tl,
          doc_url: '' // Clear filename, will be updated after upload
        })),
        lease_contract: {
          ...leaseContract,
          doc_url: '' // Clear filename, will be updated after upload
        },
        // For backwards compatibility, set primary trade license
        trade_license_no: tradeLicenses[0]?.license_no || '',
        trade_license_expiry: tradeLicenses[0]?.expiry || '',
        trade_license_city: tradeLicenses[0]?.city || '',
        trade_license_doc_url: '',
        // Clear doc URLs - they'll be set after upload
        trn_doc_url: '',
        owner_emirates_id_doc_url: '',
        owner_passport_doc_url: ''
      };
      
      let lesseeId = editingId;
      
      if (editingId) {
        await axios.put(`${API}/lessees/${editingId}`, submitData, {
          headers: getAuthHeader()
        });
      } else {
        const response = await axios.post(`${API}/lessees`, submitData, {
          headers: getAuthHeader()
        });
        lesseeId = response.data.id;
      }
      
      // Check if there are any files to upload
      const hasFilesToUpload = pendingFiles.trn || 
        pendingFiles.owner_emirates_id || 
        pendingFiles.owner_passport || 
        pendingFiles.lease_contract ||
        pendingFiles.trade_licenses.some(f => f !== null);
      
      if (hasFilesToUpload && lesseeId) {
        toast.info('Uploading documents...', { duration: 2000 });
        const uploadResults = await uploadAllPendingDocuments(lesseeId);
        
        if (uploadResults.success.length > 0) {
          toast.success(`Uploaded ${uploadResults.success.length} document(s) successfully`);
        }
        if (uploadResults.failed.length > 0) {
          toast.error(`Failed to upload: ${uploadResults.failed.join(', ')}`);
        }
      }
      
      toast.success(editingId ? 'Lessee updated successfully' : 'Lessee added successfully');
      setDialogOpen(false);
      resetForm();
      fetchLessees();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    } finally {
      setUploadingDocs(false);
    }
  };

  const handleEdit = (lessee) => {
    setEditingId(lessee.id);
    
    // Load trade licenses
    if (lessee.trade_licenses && lessee.trade_licenses.length > 0) {
      setTradeLicenses(lessee.trade_licenses);
    } else {
      // Backwards compatibility - convert single trade license to array
      setTradeLicenses([{
        city: lessee.trade_license_city || '',
        license_no: lessee.trade_license_no || lessee.trade_license_number || '',
        expiry: lessee.trade_license_expiry || '',
        doc_url: lessee.trade_license_doc_url || ''
      }]);
    }
    
    // Load lease contract
    setLeaseContract(lessee.lease_contract || {
      doc_url: '',
      start_date: '',
      end_date: ''
    });
    
    setFormData({
      company_name: lessee.company_name,
      address: lessee.address,
      trn_no: lessee.trn_no || lessee.trn_number,
      owner_name: lessee.owner_name,
      owner_emirates_id: lessee.owner_emirates_id,
      owner_passport_no: lessee.owner_passport_no,
      // Contact Person fields
      contact_person_name: lessee.contact_person_name || '',
      contact_email: lessee.contact_email || lessee.owner_email || '',
      contact_mobile: lessee.contact_mobile || lessee.owner_mobile || '',
      contact_designation: lessee.contact_designation || '',
      status: lessee.status,
      lessee_type: lessee.lessee_type || 'external',
      maintenance_contract: lessee.maintenance_contract || 'non_amc',
      trn_doc_url: lessee.trn_doc_url || '',
      owner_emirates_id_doc_url: lessee.owner_emirates_id_doc_url || '',
      owner_passport_doc_url: lessee.owner_passport_doc_url || ''
    });
    
    // When editing, skip the type selection and go to step 1
    setLesseeType(lessee.lessee_type || 'external');
    setCurrentStep(1);
    setStepValidation({ company: true, owner: true, documents: true });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this lessee?')) {
      try {
        await axios.delete(`${API}/lessees/${id}`, {
          headers: getAuthHeader()
        });
        toast.success('Lessee deleted successfully');
        fetchLessees();
      } catch (error) {
        toast.error('Failed to delete lessee');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '', address: '', trn_no: '', owner_name: '', 
      owner_emirates_id: '', owner_passport_no: '', 
      contact_person_name: '', contact_email: '', contact_mobile: '', contact_designation: '',
      status: 'active', lessee_type: '', maintenance_contract: 'non_amc',
      trn_doc_url: '', owner_emirates_id_doc_url: '', owner_passport_doc_url: ''
    });
    setTradeLicenses([{ city: '', license_no: '', expiry: '', doc_url: '' }]);
    setLeaseContract({ doc_url: '', start_date: '', end_date: '' });
    setEditingId(null);
    setCurrentStep(0);
    setLesseeType('');
    setStepValidation({ company: false, owner: false, documents: false });
    setValidationErrors({ trn_no: '', contact_mobile: '' });
    // Reset pending files
    setPendingFiles({
      trn: null,
      owner_emirates_id: null,
      owner_passport: null,
      lease_contract: null,
      trade_licenses: []
    });
  };

  // Trade license management
  const addTradeLicense = () => {
    if (tradeLicenses.length < 5) {
      setTradeLicenses([...tradeLicenses, { city: '', license_no: '', expiry: '', doc_url: '' }]);
    } else {
      toast.error('Maximum 5 trade licenses allowed per lessee');
    }
  };

  const removeTradeLicense = (index) => {
    if (tradeLicenses.length > 1) {
      setTradeLicenses(tradeLicenses.filter((_, i) => i !== index));
    }
  };

  const updateTradeLicense = (index, field, value) => {
    const updated = [...tradeLicenses];
    updated[index][field] = value;
    setTradeLicenses(updated);
  };

  // Handle TRN validation on blur
  const handleTRNBlur = () => {
    if (formData.trn_no && !validateTRN(formData.trn_no)) {
      setValidationErrors(prev => ({
        ...prev,
        trn_no: 'TRN must be 15 digits'
      }));
    } else {
      setValidationErrors(prev => ({ ...prev, trn_no: '' }));
    }
  };

  // Handle mobile validation and formatting
  const handleMobileChange = (value) => {
    const formatted = formatUAEMobile(value);
    setFormData({ ...formData, contact_mobile: formatted });
    
    if (formatted.length > 8 && !validateUAEMobile(formatted)) {
      setValidationErrors(prev => ({
        ...prev,
        contact_mobile: 'Invalid UAE mobile number format'
      }));
    } else {
      setValidationErrors(prev => ({ ...prev, contact_mobile: '' }));
    }
  };

  // Validation functions for each step
  const validateCompanyStep = () => {
    // Check basic fields
    if (!formData.company_name.trim() || !formData.address.trim() || !formData.trn_no.trim()) {
      return false;
    }
    
    // Check TRN format
    if (!validateTRN(formData.trn_no)) {
      setValidationErrors(prev => ({ ...prev, trn_no: 'TRN must be 15 digits' }));
      return false;
    }
    
    // Check at least one trade license
    const validLicenses = tradeLicenses.filter(tl => tl.city && tl.license_no && tl.expiry);
    if (validLicenses.length === 0) {
      toast.error('At least one complete trade license is required');
      return false;
    }
    
    return true;
  };

  const validateOwnerStep = () => {
    // Owner fields
    if (!formData.owner_name.trim() || !formData.owner_emirates_id.trim() || 
        !formData.owner_passport_no.trim()) {
      return false;
    }
    
    // Contact Person fields (required)
    if (!formData.contact_person_name.trim() || !formData.contact_email.trim() || 
        !formData.contact_mobile.trim()) {
      return false;
    }
    
    // Check mobile format
    if (!validateUAEMobile(formData.contact_mobile)) {
      setValidationErrors(prev => ({ ...prev, contact_mobile: 'Invalid UAE mobile number format' }));
      return false;
    }
    
    return true;
  };

  const validateDocumentsStep = () => {
    // Check trade license documents
    const licensesWithDocs = tradeLicenses.filter(tl => tl.city && tl.license_no && tl.doc_url);
    const validLicenses = tradeLicenses.filter(tl => tl.city && tl.license_no);
    
    if (licensesWithDocs.length < validLicenses.length) {
      toast.error('Please upload documents for all trade licenses');
      return false;
    }
    
    // Check other required documents
    if (!formData.trn_doc_url || !formData.owner_emirates_id_doc_url || !formData.owner_passport_doc_url) {
      return false;
    }
    
    // Check lease contract
    if (!leaseContract.doc_url || !leaseContract.start_date || !leaseContract.end_date) {
      toast.error('Lease contract with start and end dates is required');
      return false;
    }
    
    return true;
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!validateCompanyStep()) {
        toast.error('Please fill all required fields correctly');
        return;
      }
      setStepValidation(prev => ({ ...prev, company: true }));
    } else if (currentStep === 2) {
      if (!validateOwnerStep()) {
        toast.error('Please fill all required fields correctly');
        return;
      }
      setStepValidation(prev => ({ ...prev, owner: true }));
    } else if (currentStep === 3) {
      if (!validateDocumentsStep()) {
        toast.error('Please upload all required documents');
        return;
      }
      setStepValidation(prev => ({ ...prev, documents: true }));
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleLesseeTypeSelect = (type) => {
    setLesseeType(type);
    setFormData(prev => ({ ...prev, lessee_type: type }));
    setCurrentStep(1);
  };

  const handleViewDetails = (lessee) => {
    setSelectedLessee(lessee);
    setDetailDialogOpen(true);
  };

  // Helper function to check if a URL is valid and downloadable
  const isValidDocumentUrl = (url) => {
    // Consider any non-empty url as valid - we'll handle the URL construction in getDocumentUrl
    return !!url && url.trim() !== '';
  };

  // Helper function to get proper download URL
  const getDocumentUrl = (url) => {
    if (!url) return null;
    // Already a full URL
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    // API path
    if (url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    // For filenames or relative paths, construct the full URL
    // Try to determine if it's a lessee document based on pattern
    if (url.includes('/uploads/lessees/') || url.startsWith('uploads/lessees/')) {
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
    }
    // For OneDrive shareable links stored as filename, we need to use the stored URL
    // Return the URL as-is and let the browser/OneDrive handle it
    return url;
  };

  const handleDownloadAllDocuments = (lessee) => {
    // Collect all available document URLs
    const documents = [];
    
    // Trade license documents
    if (lessee.trade_licenses && lessee.trade_licenses.length > 0) {
      lessee.trade_licenses.forEach((tl, index) => {
        if (tl.doc_url) {
          const url = getDocumentUrl(tl.doc_url);
          if (url) {
            documents.push({ name: `Trade_License_${tl.city || index + 1}`, url });
          }
        }
      });
    } else if (lessee.trade_license_doc_url) {
      const url = getDocumentUrl(lessee.trade_license_doc_url);
      if (url) {
        documents.push({ name: 'Trade_License', url });
      }
    }
    
    // TRN document
    if (lessee.trn_doc_url) {
      const url = getDocumentUrl(lessee.trn_doc_url);
      if (url) {
        documents.push({ name: 'TRN_Certificate', url });
      }
    }
    
    // Owner documents
    if (lessee.owner_emirates_id_doc_url) {
      const url = getDocumentUrl(lessee.owner_emirates_id_doc_url);
      if (url) {
        documents.push({ name: 'Owner_Emirates_ID', url });
      }
    }
    if (lessee.owner_passport_doc_url) {
      const url = getDocumentUrl(lessee.owner_passport_doc_url);
      if (url) {
        documents.push({ name: 'Owner_Passport', url });
      }
    }
    
    // Lease contract
    if (lessee.lease_contract?.doc_url) {
      const url = getDocumentUrl(lessee.lease_contract.doc_url);
      if (url) {
        documents.push({ name: 'Lease_Contract', url });
      }
    }
    
    if (documents.length === 0) {
      toast.error('No documents available for this lessee');
      return;
    }
    
    // Download each valid document
    documents.forEach((doc, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = doc.url;
        link.download = `${lessee.company_name}_${doc.name}`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 300); // Stagger downloads to avoid browser blocking
    });
    
    toast.success(`Downloading ${documents.length} document(s)...`);
  };

  // State for tracking document upload in detail view
  const [detailUploadingDoc, setDetailUploadingDoc] = useState(null);

  // Handle document upload from detail view
  const handleDetailDocUpload = async (e, documentType, tradeLicenseIndex = null) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLessee) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPG, PNG, WEBP, PDF');
      return;
    }
    
    // Validate file size (15MB max)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File size exceeds 15MB limit');
      return;
    }
    
    setDetailUploadingDoc(documentType + (tradeLicenseIndex !== null ? `_${tradeLicenseIndex}` : ''));
    
    try {
      const result = await uploadLesseeDocument(selectedLessee.id, documentType, file, tradeLicenseIndex);
      toast.success(`${result.filename} uploaded successfully`);
      
      // Update selected lessee with new URL
      const updatedLessee = { ...selectedLessee };
      if (documentType === 'trade_license' && tradeLicenseIndex !== null) {
        if (updatedLessee.trade_licenses && updatedLessee.trade_licenses[tradeLicenseIndex]) {
          updatedLessee.trade_licenses[tradeLicenseIndex].doc_url = result.url;
        }
      } else if (documentType === 'trn') {
        updatedLessee.trn_doc_url = result.url;
      } else if (documentType === 'owner_emirates_id') {
        updatedLessee.owner_emirates_id_doc_url = result.url;
      } else if (documentType === 'owner_passport') {
        updatedLessee.owner_passport_doc_url = result.url;
      } else if (documentType === 'lease_contract') {
        if (!updatedLessee.lease_contract) updatedLessee.lease_contract = {};
        updatedLessee.lease_contract.doc_url = result.url;
      }
      
      setSelectedLessee(updatedLessee);
      
      // Refresh lessees list
      fetchLessees();
    } catch (error) {
      toast.error(`Failed to upload document: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDetailUploadingDoc(null);
    }
  };

  // Upload a single document for a lessee
  const uploadLesseeDocument = async (lesseeId, documentType, file, tradeLicenseIndex = null) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    if (tradeLicenseIndex !== null) {
      formData.append('trade_license_index', tradeLicenseIndex.toString());
    }
    
    try {
      const response = await axios.post(
        `${API}/lessees/${lesseeId}/upload-document`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error);
      throw error;
    }
  };

  // Upload all pending documents for a lessee
  const uploadAllPendingDocuments = async (lesseeId) => {
    const results = { success: [], failed: [] };
    
    // Upload TRN document
    if (pendingFiles.trn) {
      try {
        await uploadLesseeDocument(lesseeId, 'trn', pendingFiles.trn);
        results.success.push('TRN Certificate');
      } catch (e) {
        results.failed.push('TRN Certificate');
      }
    }
    
    // Upload Owner Emirates ID
    if (pendingFiles.owner_emirates_id) {
      try {
        await uploadLesseeDocument(lesseeId, 'owner_emirates_id', pendingFiles.owner_emirates_id);
        results.success.push('Owner Emirates ID');
      } catch (e) {
        results.failed.push('Owner Emirates ID');
      }
    }
    
    // Upload Owner Passport
    if (pendingFiles.owner_passport) {
      try {
        await uploadLesseeDocument(lesseeId, 'owner_passport', pendingFiles.owner_passport);
        results.success.push('Owner Passport');
      } catch (e) {
        results.failed.push('Owner Passport');
      }
    }
    
    // Upload Lease Contract
    if (pendingFiles.lease_contract) {
      try {
        await uploadLesseeDocument(lesseeId, 'lease_contract', pendingFiles.lease_contract);
        results.success.push('Lease Contract');
      } catch (e) {
        results.failed.push('Lease Contract');
      }
    }
    
    // Upload Trade License documents
    for (let i = 0; i < pendingFiles.trade_licenses.length; i++) {
      const file = pendingFiles.trade_licenses[i];
      if (file) {
        try {
          await uploadLesseeDocument(lesseeId, 'trade_license', file, i);
          results.success.push(`Trade License ${i + 1}`);
        } catch (e) {
          results.failed.push(`Trade License ${i + 1}`);
        }
      }
    }
    
    return results;
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Map fieldName to pending file key
      const fileKeyMap = {
        'trn_doc_url': 'trn',
        'owner_emirates_id_doc_url': 'owner_emirates_id',
        'owner_passport_doc_url': 'owner_passport'
      };
      
      const fileKey = fileKeyMap[fieldName];
      if (fileKey) {
        setPendingFiles(prev => ({ ...prev, [fileKey]: file }));
      }
      
      // Also update formData to show filename in UI
      setFormData({ ...formData, [fieldName]: file.name });
      toast.success(`${file.name} selected for upload`);
    }
  };

  const handleTradeLicenseFileUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      // Store actual file for upload
      setPendingFiles(prev => {
        const newTradeLicenses = [...prev.trade_licenses];
        newTradeLicenses[index] = file;
        return { ...prev, trade_licenses: newTradeLicenses };
      });
      
      // Update UI to show filename
      updateTradeLicense(index, 'doc_url', file.name);
      toast.success(`${file.name} selected for upload`);
    }
  };

  const handleLeaseContractFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPendingFiles(prev => ({ ...prev, lease_contract: file }));
      setLeaseContract(prev => ({ ...prev, doc_url: file.name }));
      toast.success(`${file.name} selected for upload`);
    }
  };

  // Get cities from trade licenses for display
  const getLicenseCities = (lessee) => {
    if (lessee.trade_licenses && lessee.trade_licenses.length > 0) {
      return lessee.trade_licenses.map(tl => tl.city).filter(Boolean).join(', ');
    }
    return lessee.trade_license_city || '-';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="lessees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('lessees.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('lessees.subtitle')}</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-lime-500 to-green-600" size={isMobile ? "sm" : "default"} data-testid="add-lessee-btn">
                <Plus className="w-4 h-4 mr-2" />
                {isMobile ? 'Add' : t('lessees.addLessee')}
              </Button>
            </DialogTrigger>
          <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {editingId ? t('lessees.editLessee') : t('lessees.addLessee')}
              </DialogTitle>
              <DialogDescription>
                {currentStep === 0 ? 'Select the type of lessee' : 
                 currentStep === 4 ? 'Review and submit lessee information' :
                 `Step ${currentStep} of 4`}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Indicator */}
            {currentStep > 0 && (
              <div className="flex items-center justify-between mb-4 px-4">
                {['Company', 'Owner', 'Documents', 'Review'].map((step, index) => (
                  <div key={step} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      currentStep > index + 1 ? 'bg-green-500' : 
                      currentStep === index + 1 ? 'bg-lime-500' : 'bg-gray-200'
                    }`}>
                      {currentStep > index + 1 ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <span className={`text-sm font-medium ${currentStep === index + 1 ? 'text-white' : 'text-gray-500'}`}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <span className={`ml-2 text-sm ${currentStep === index + 1 ? 'font-medium text-lime-600' : 'text-gray-500'}`}>
                      {step}
                    </span>
                    {index < 3 && <ChevronRight className="w-4 h-4 mx-2 text-gray-300" />}
                  </div>
                ))}
              </div>
            )}

            {/* Step 0: Type Selection */}
            {currentStep === 0 && (
              <div className="space-y-4 py-6">
                <p className="text-center text-slate-600 mb-6">What type of lessee would you like to add?</p>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-32 flex flex-col items-center justify-center gap-2 hover:border-lime-500 hover:bg-lime-50"
                    onClick={() => handleLesseeTypeSelect('in-house')}
                    data-testid="lessee-type-inhouse"
                  >
                    <Home className="w-8 h-8 text-lime-600" />
                    <span className="font-medium">In-House</span>
                    <span className="text-xs text-slate-500">Company-owned fleet</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-32 flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50"
                    onClick={() => handleLesseeTypeSelect('external')}
                    data-testid="lessee-type-external"
                  >
                    <Building className="w-8 h-8 text-blue-600" />
                    <span className="font-medium">External</span>
                    <span className="text-xs text-slate-500">Third-party lessee</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Step 1: Company Information */}
            {currentStep === 1 && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Lessee Type:</Label>
                    <Select 
                      value={lesseeType} 
                      onValueChange={(value) => {
                        setLesseeType(value);
                        setFormData(prev => ({ ...prev, lessee_type: value }));
                      }}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue>
                          <Badge className={lesseeType === 'in-house' ? 'bg-lime-100 text-lime-700' : 'bg-blue-100 text-blue-700'}>
                            {lesseeType === 'in-house' ? '🏠 In-House' : '🏢 External'}
                          </Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-house">🏠 In-House</SelectItem>
                        <SelectItem value="external">🏢 External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600">Maintenance:</Label>
                    <Select 
                      value={formData.maintenance_contract || 'non_amc'} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, maintenance_contract: value }))}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue>
                          <span className={formData.maintenance_contract === 'amc' ? 'text-green-700 font-semibold' : 'text-red-600 font-medium'}>
                            {formData.maintenance_contract === 'amc' ? 'AMC' : 'Non-AMC'}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amc">AMC (Annual Maintenance Contract)</SelectItem>
                        <SelectItem value="non_amc">Non-AMC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Company Name *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="e.g., Express Delivery LLC"
                      data-testid="lessee-company-name"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Address *</Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Complete address with building, street, area, emirate"
                      data-testid="lessee-address"
                    />
                  </div>
                  
                  {/* TRN Field with Validation */}
                  <div className="space-y-2">
                    <Label>TRN (Tax Registration Number) *</Label>
                    <Input
                      value={formData.trn_no}
                      onChange={(e) => setFormData({ ...formData, trn_no: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      onBlur={handleTRNBlur}
                      placeholder="100123456700003"
                      data-testid="lessee-trn"
                      className={validationErrors.trn_no ? 'border-red-500' : ''}
                    />
                    {validationErrors.trn_no && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.trn_no}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">15 digits</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="lessee-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">{t('lessees.statusActive')}</SelectItem>
                        <SelectItem value="inactive">{t('lessees.statusInactive')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Trade Licenses Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Trade Licenses ({tradeLicenses.length}/5)
                    </Label>
                    {tradeLicenses.length < 5 && (
                      <Button type="button" variant="outline" size="sm" onClick={addTradeLicense}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add License
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    Add trade licenses for each emirate where you operate. Bikes can only be assigned based on matching license city.
                  </p>
                  
                  {tradeLicenses.map((license, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3 mb-3 relative">
                      {tradeLicenses.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 h-6 w-6 p-0"
                          onClick={() => removeTradeLicense(index)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">City/Emirate *</Label>
                          <Select 
                            value={license.city} 
                            onValueChange={(value) => updateTradeLicense(index, 'city', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select emirate" />
                            </SelectTrigger>
                            <SelectContent>
                              {UAE_EMIRATES.map(emirate => (
                                <SelectItem key={emirate} value={emirate}>{emirate}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">License No. *</Label>
                          <Input
                            value={license.license_no}
                            onChange={(e) => updateTradeLicense(index, 'license_no', e.target.value)}
                            placeholder="TL-123456"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Expiry Date *</Label>
                          <Input
                            type="date"
                            value={license.expiry}
                            onChange={(e) => updateTradeLicense(index, 'expiry', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Owner & Contact Person Details */}
            {currentStep === 2 && (
              <div className="space-y-6 py-4">
                {/* Owner Details Section */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Owner Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label>Owner Name *</Label>
                      <Input
                        value={formData.owner_name}
                        onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                        placeholder="Full name of company owner"
                        data-testid="lessee-owner-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Owner Emirates ID *</Label>
                      <Input
                        value={formData.owner_emirates_id}
                        onChange={(e) => setFormData({ ...formData, owner_emirates_id: e.target.value })}
                        placeholder="784-1234-1234567-1"
                        data-testid="lessee-owner-emirates-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Owner Passport No. *</Label>
                      <Input
                        value={formData.owner_passport_no}
                        onChange={(e) => setFormData({ ...formData, owner_passport_no: e.target.value })}
                        placeholder="AB1234567"
                        data-testid="lessee-owner-passport"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Person Section */}
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Person (Primary Communication)
                  </h4>
                  <p className="text-xs text-green-700 mb-4">
                    All system notifications, critical alerts, and communications will be sent to this contact.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Person Name *</Label>
                      <Input
                        value={formData.contact_person_name}
                        onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                        placeholder="John Doe"
                        data-testid="lessee-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Designation</Label>
                      <Input
                        value={formData.contact_designation}
                        onChange={(e) => setFormData({ ...formData, contact_designation: e.target.value })}
                        placeholder="Operations Manager"
                        data-testid="lessee-contact-designation"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Email *</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        placeholder="contact@company.com"
                        data-testid="lessee-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Mobile *</Label>
                      <Input
                        value={formData.contact_mobile}
                        onChange={(e) => handleMobileChange(e.target.value)}
                        placeholder="+971 50 123 4567"
                        data-testid="lessee-mobile"
                        className={validationErrors.contact_mobile ? 'border-red-500' : ''}
                      />
                      {validationErrors.contact_mobile && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {validationErrors.contact_mobile}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">UAE format: +971 5X XXX XXXX</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Documents Upload */}
            {currentStep === 3 && (
              <div className="space-y-4 py-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800">⚠️ All documents are required to complete the lessee registration.</p>
                </div>
                
                {/* Trade License Documents */}
                <div className="border-b pb-4 mb-4">
                  <Label className="text-base font-semibold mb-3 block">Trade License Documents</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {tradeLicenses.filter(tl => tl.city && tl.license_no).map((license, index) => (
                      <div key={index} className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4" />
                          {license.city} License *
                        </Label>
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleTradeLicenseFileUpload(e, index)}
                        />
                        {license.doc_url && <p className="text-xs text-green-600">✓ {license.doc_url}</p>}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Lease Contract - Mandatory */}
                <div className="border-b pb-4 mb-4 bg-blue-50 rounded-lg p-4">
                  <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Lease Contract * (Required)
                  </Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Contract Document *</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleLeaseContractFileUpload}
                      />
                      {leaseContract.doc_url && <p className="text-xs text-green-600">✓ {leaseContract.doc_url}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Contract Start Date *</Label>
                      <Input
                        type="date"
                        value={leaseContract.start_date}
                        onChange={(e) => setLeaseContract(prev => ({ ...prev, start_date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Contract End Date *</Label>
                      <Input
                        type="date"
                        value={leaseContract.end_date}
                        onChange={(e) => setLeaseContract(prev => ({ ...prev, end_date: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Other Documents */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <File className="w-4 h-4" />
                      TRN Certificate *
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e, 'trn_doc_url')}
                      data-testid="lessee-trn-upload"
                    />
                    {formData.trn_doc_url && <p className="text-xs text-green-600">✓ {formData.trn_doc_url}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Owner Emirates ID *
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e, 'owner_emirates_id_doc_url')}
                      data-testid="lessee-owner-emirates-upload"
                    />
                    {formData.owner_emirates_id_doc_url && <p className="text-xs text-green-600">✓ {formData.owner_emirates_id_doc_url}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <File className="w-4 h-4" />
                      Owner Passport *
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e, 'owner_passport_doc_url')}
                      data-testid="lessee-owner-passport-upload"
                    />
                    {formData.owner_passport_doc_url && <p className="text-xs text-green-600">✓ {formData.owner_passport_doc_url}</p>}
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-green-800">Note: In production, files will be uploaded to cloud storage. Currently showing file names for demonstration.</p>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {currentStep === 4 && (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 font-medium">✅ All steps completed! Review the information below and click "Add Lessee" to submit.</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Company Information</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><span className="text-slate-500">Type:</span> <Badge className={lesseeType === 'in-house' ? 'bg-lime-100 text-lime-700' : 'bg-blue-100 text-blue-700'}>{lesseeType}</Badge></p>
                        <p><span className="text-slate-500">Company:</span> {formData.company_name}</p>
                        <p><span className="text-slate-500">TRN:</span> {formData.trn_no}</p>
                        <div className="mt-2">
                          <p className="text-slate-500 mb-1">Trade Licenses:</p>
                          {tradeLicenses.filter(tl => tl.city).map((tl, i) => (
                            <Badge key={i} variant="outline" className="mr-1 mb-1">
                              <MapPin className="w-3 h-3 mr-1" />
                              {tl.city}: {tl.license_no}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Owner Details</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><span className="text-slate-500">Name:</span> {formData.owner_name}</p>
                        <p><span className="text-slate-500">Emirates ID:</span> {formData.owner_emirates_id}</p>
                        <p><span className="text-slate-500">Passport:</span> {formData.owner_passport_no}</p>
                      </CardContent>
                    </Card>
                    {/* Contact Person Card */}
                    <Card className="border-green-200 bg-green-50">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                          <Mail className="w-4 h-4" />
                          Contact Person
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><span className="text-slate-500">Name:</span> {formData.contact_person_name}</p>
                        {formData.contact_designation && (
                          <p><span className="text-slate-500">Designation:</span> {formData.contact_designation}</p>
                        )}
                        <p><span className="text-slate-500">Email:</span> {formData.contact_email}</p>
                        <p><span className="text-slate-500">Mobile:</span> {formData.contact_mobile}</p>
                        <p className="text-xs text-green-700 mt-2 pt-2 border-t border-green-200">
                          📧 System notifications will be sent to this contact
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Lease Contract</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm">
                        <div className="flex gap-4">
                          <p><span className="text-slate-500">Document:</span> {leaseContract.doc_url || 'Not uploaded'}</p>
                          <p><span className="text-slate-500">Start:</span> {formatDateDisplay(leaseContract.start_date)}</p>
                          <p><span className="text-slate-500">End:</span> {formatDateDisplay(leaseContract.end_date)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex justify-between mt-6">
                    <Button type="button" variant="outline" onClick={handlePrevStep}>
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-lime-500 to-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {editingId ? 'Update Lessee' : 'Add Lessee'}
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {/* Navigation Buttons (Steps 1-3) */}
            {currentStep > 0 && currentStep < 4 && (
              <div className="flex justify-between mt-6">
                <Button type="button" variant="outline" onClick={handlePrevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {currentStep === 1 ? 'Cancel' : 'Back'}
                </Button>
                <Button type="button" onClick={handleNextStep} className="bg-gradient-to-r from-lime-500 to-green-600">
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by company name, owner, email, mobile, or license number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lessees Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t('lessees.title')} ({filteredLessees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>AMC</TableHead>
                  <TableHead>Cities</TableHead>
                  <TableHead>TRN</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Contract End</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLessees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500 py-8">
                      No lessee customers found. Add your first lessee to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLessees.map((lessee) => (
                    <TableRow 
                      key={lessee.id} 
                      data-testid={`lessee-row-${lessee.id}`}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleViewDetails(lessee)}
                    >
                      <TableCell className="font-medium">{lessee.company_name}</TableCell>
                      <TableCell>
                        <Badge className={lessee.lessee_type === 'in-house' ? 'bg-lime-100 text-lime-700' : 'bg-blue-100 text-blue-700'}>
                          {lessee.lessee_type === 'in-house' ? 'In-House' : 'External'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={lessee.maintenance_contract === 'amc' ? 'text-green-700 font-semibold' : 'text-red-600 font-medium'}>
                          {lessee.maintenance_contract === 'amc' ? 'AMC' : 'Non-AMC'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {(lessee.trade_licenses || []).length > 0 ? (
                            <>
                              <Badge variant="outline" className="text-xs">
                                {lessee.trade_licenses[0].city}
                              </Badge>
                              {lessee.trade_licenses.length > 1 && (
                                <span className="text-xs text-slate-500 font-medium">
                                  +{lessee.trade_licenses.length - 1}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-400 text-xs">{lessee.trade_license_city || '-'}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{lessee.trn_no || lessee.trn_number || '-'}</TableCell>
                      <TableCell>{lessee.owner_name || '-'}</TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <div>{lessee.contact_email || lessee.owner_email || '-'}</div>
                          <div className="text-slate-500">{lessee.contact_mobile || lessee.owner_mobile || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {lessee.lease_contract?.end_date ? formatDateDisplay(lessee.lease_contract.end_date) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={lessee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {lessee.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`lessee-actions-${lessee.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {canEdit && (
                                <DropdownMenuItem 
                                  onClick={() => handleEdit(lessee)}
                                  className="cursor-pointer"
                                  data-testid={`edit-lessee-${lessee.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit Lessee
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(lessee.id)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Lessee
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => handleViewDetails(lessee)}
                                className="cursor-pointer"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Lessee Documents
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDownloadAllDocuments(lessee)}
                                className="cursor-pointer"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download Lessee Documents
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {selectedLessee && (
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {selectedLessee.company_name}
              </DialogTitle>
              <DialogDescription>Lessee Details</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4 overflow-hidden">
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Company Information</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p><span className="text-slate-500">Type:</span> {selectedLessee.lessee_type || 'External'}</p>
                  <p className="flex items-center gap-2">
                    <span className="text-slate-500">Maintenance:</span> 
                    <span className={selectedLessee.maintenance_contract === 'amc' ? 'text-green-700 font-semibold' : 'text-red-600 font-medium'}>
                      {selectedLessee.maintenance_contract === 'amc' ? 'AMC' : 'Non-AMC'}
                    </span>
                  </p>
                  <p><span className="text-slate-500">Address:</span> {selectedLessee.address}</p>
                  <p><span className="text-slate-500">TRN:</span> {selectedLessee.trn_no || selectedLessee.trn_number}</p>
                  <div className="mt-2">
                    <p className="text-slate-500 mb-1">Trade Licenses:</p>
                    {(selectedLessee.trade_licenses || []).map((tl, i) => (
                      <div key={i} className="bg-slate-50 rounded p-2 mb-1 text-xs">
                        <span className="font-medium">{tl.city}:</span> {tl.license_no} (Exp: {formatDateDisplay(tl.expiry)})
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Owner Details</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <p><span className="text-slate-500">Name:</span> {selectedLessee.owner_name}</p>
                  <p><span className="text-slate-500">Emirates ID:</span> {selectedLessee.owner_emirates_id}</p>
                  <p><span className="text-slate-500">Passport:</span> {selectedLessee.owner_passport_no}</p>
                  <p><span className="text-slate-500">Email:</span> {selectedLessee.contact_email || selectedLessee.owner_email}</p>
                  <p><span className="text-slate-500">Mobile:</span> {selectedLessee.contact_mobile || selectedLessee.owner_mobile}</p>
                </CardContent>
              </Card>
              {selectedLessee.lease_contract && (
                <Card className="col-span-2">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Lease Contract</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <div className="flex flex-wrap gap-4">
                      <p><span className="text-slate-500">Start Date:</span> {formatDateDisplay(selectedLessee.lease_contract.start_date)}</p>
                      <p><span className="text-slate-500">End Date:</span> {formatDateDisplay(selectedLessee.lease_contract.end_date)}</p>
                      <p className="flex items-center gap-1">
                        <span className="text-slate-500">Document:</span> 
                        {selectedLessee.lease_contract.doc_url ? (
                          <span className="text-green-600 text-xs">Uploaded</span>
                        ) : (
                          <span className="text-amber-600 text-xs">Not uploaded</span>
                        )}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Documents Section */}
              <Card className="col-span-2 border-blue-200 bg-blue-50">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documents
                    </span>
                    {canDownloadDocuments && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 gap-1"
                        onClick={() => handleDownloadAllDocuments(selectedLessee)}
                      >
                        <Download className="w-3 h-3" />
                        Download All
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Trade License Documents */}
                    {(selectedLessee.trade_licenses || []).map((tl, i) => (
                      <div key={`tl-${i}`} className="flex items-center justify-between bg-white rounded p-2 border">
                        <div className="flex flex-col flex-1">
                          <span className="text-xs text-slate-600">Trade License - {tl.city}</span>
                          {tl.doc_url && (
                            <span className="text-[10px] text-green-600">Document available</span>
                          )}
                          {!tl.doc_url && (
                            <span className="text-[10px] text-slate-400">No document</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {tl.doc_url && canViewDocuments && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => window.open(getDocumentUrl(tl.doc_url), '_blank')}
                              title="View Document"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          )}
                          {tl.doc_url && canDownloadDocuments && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = getDocumentUrl(tl.doc_url);
                                link.download = `Trade_License_${tl.city}.pdf`;
                                link.target = '_blank';
                                link.click();
                              }}
                              title="Download Document"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          )}
                          {canReuploadDocuments && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf,.webp"
                                className="hidden"
                                onChange={(e) => handleDetailDocUpload(e, 'trade_license', i)}
                                disabled={detailUploadingDoc === `trade_license_${i}`}
                              />
                              <div className={`h-6 px-2 inline-flex items-center justify-center rounded text-xs ${detailUploadingDoc === `trade_license_${i}` ? 'bg-slate-100' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} title={tl.doc_url ? "Re-upload Document" : "Upload Document"}>
                                {detailUploadingDoc === `trade_license_${i}` ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Upload className="w-3 h-3" />
                                )}
                              </div>
                            </label>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* TRN Document */}
                    <div className="flex items-center justify-between bg-white rounded p-2 border">
                      <div className="flex flex-col flex-1">
                        <span className="text-xs text-slate-600">TRN Certificate</span>
                        {selectedLessee.trn_doc_url && (
                          <span className="text-[10px] text-green-600">Document available</span>
                        )}
                        {!selectedLessee.trn_doc_url && (
                          <span className="text-[10px] text-slate-400">No document</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedLessee.trn_doc_url && canViewDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => window.open(getDocumentUrl(selectedLessee.trn_doc_url), '_blank')}
                            title="View Document"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        )}
                        {selectedLessee.trn_doc_url && canDownloadDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = getDocumentUrl(selectedLessee.trn_doc_url);
                              link.download = 'TRN_Certificate.pdf';
                              link.target = '_blank';
                              link.click();
                            }}
                            title="Download Document"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                        {canReuploadDocuments && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.webp"
                              className="hidden"
                              onChange={(e) => handleDetailDocUpload(e, 'trn')}
                              disabled={detailUploadingDoc === 'trn'}
                            />
                            <div className={`h-6 px-2 inline-flex items-center justify-center rounded text-xs ${detailUploadingDoc === 'trn' ? 'bg-slate-100' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} title={selectedLessee.trn_doc_url ? "Re-upload Document" : "Upload Document"}>
                              {detailUploadingDoc === 'trn' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                    
                    {/* Owner Emirates ID */}
                    <div className="flex items-center justify-between bg-white rounded p-2 border">
                      <div className="flex flex-col flex-1">
                        <span className="text-xs text-slate-600">Owner Emirates ID</span>
                        {selectedLessee.owner_emirates_id_doc_url && (
                          <span className="text-[10px] text-green-600">Document available</span>
                        )}
                        {!selectedLessee.owner_emirates_id_doc_url && (
                          <span className="text-[10px] text-slate-400">No document</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedLessee.owner_emirates_id_doc_url && canViewDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => window.open(getDocumentUrl(selectedLessee.owner_emirates_id_doc_url), '_blank')}
                            title="View Document"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        )}
                        {selectedLessee.owner_emirates_id_doc_url && canDownloadDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = getDocumentUrl(selectedLessee.owner_emirates_id_doc_url);
                              link.download = 'Owner_Emirates_ID.pdf';
                              link.target = '_blank';
                              link.click();
                            }}
                            title="Download Document"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                        {canReuploadDocuments && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.webp"
                              className="hidden"
                              onChange={(e) => handleDetailDocUpload(e, 'owner_emirates_id')}
                              disabled={detailUploadingDoc === 'owner_emirates_id'}
                            />
                            <div className={`h-6 px-2 inline-flex items-center justify-center rounded text-xs ${detailUploadingDoc === 'owner_emirates_id' ? 'bg-slate-100' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} title={selectedLessee.owner_emirates_id_doc_url ? "Re-upload Document" : "Upload Document"}>
                              {detailUploadingDoc === 'owner_emirates_id' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                    
                    {/* Owner Passport */}
                    <div className="flex items-center justify-between bg-white rounded p-2 border">
                      <div className="flex flex-col flex-1">
                        <span className="text-xs text-slate-600">Owner Passport</span>
                        {selectedLessee.owner_passport_doc_url && (
                          <span className="text-[10px] text-green-600">Document available</span>
                        )}
                        {!selectedLessee.owner_passport_doc_url && (
                          <span className="text-[10px] text-slate-400">No document</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedLessee.owner_passport_doc_url && canViewDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => window.open(getDocumentUrl(selectedLessee.owner_passport_doc_url), '_blank')}
                            title="View Document"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        )}
                        {selectedLessee.owner_passport_doc_url && canDownloadDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = getDocumentUrl(selectedLessee.owner_passport_doc_url);
                              link.download = 'Owner_Passport.pdf';
                              link.target = '_blank';
                              link.click();
                            }}
                            title="Download Document"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                        {canReuploadDocuments && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.webp"
                              className="hidden"
                              onChange={(e) => handleDetailDocUpload(e, 'owner_passport')}
                              disabled={detailUploadingDoc === 'owner_passport'}
                            />
                            <div className={`h-6 px-2 inline-flex items-center justify-center rounded text-xs ${detailUploadingDoc === 'owner_passport' ? 'bg-slate-100' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} title={selectedLessee.owner_passport_doc_url ? "Re-upload Document" : "Upload Document"}>
                              {detailUploadingDoc === 'owner_passport' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                    
                    {/* Lease Contract Document */}
                    <div className="flex items-center justify-between bg-white rounded p-2 border">
                      <div className="flex flex-col flex-1">
                        <span className="text-xs text-slate-600">Lease Contract</span>
                        {selectedLessee.lease_contract?.doc_url && (
                          <span className="text-[10px] text-green-600">Document available</span>
                        )}
                        {!selectedLessee.lease_contract?.doc_url && (
                          <span className="text-[10px] text-slate-400">No document</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {selectedLessee.lease_contract?.doc_url && canViewDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => window.open(getDocumentUrl(selectedLessee.lease_contract.doc_url), '_blank')}
                            title="View Document"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        )}
                        {selectedLessee.lease_contract?.doc_url && canDownloadDocuments && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = getDocumentUrl(selectedLessee.lease_contract.doc_url);
                              link.download = 'Lease_Contract.pdf';
                              link.target = '_blank';
                              link.click();
                            }}
                            title="Download Document"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                        {canReuploadDocuments && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.webp"
                              className="hidden"
                              onChange={(e) => handleDetailDocUpload(e, 'lease_contract')}
                              disabled={detailUploadingDoc === 'lease_contract'}
                            />
                            <div className={`h-6 px-2 inline-flex items-center justify-center rounded text-xs ${detailUploadingDoc === 'lease_contract' ? 'bg-slate-100' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} title={selectedLessee.lease_contract?.doc_url ? "Re-upload Document" : "Upload Document"}>
                              {detailUploadingDoc === 'lease_contract' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Document Legend */}
                  <div className="flex items-center gap-4 mt-3 pt-2 border-t border-slate-200 text-[10px] text-slate-500">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-blue-600" /> View</span>
                    <span className="flex items-center gap-1"><Download className="w-3 h-3 text-green-600" /> Download</span>
                    <span className="flex items-center gap-1"><Upload className="w-3 h-3 text-amber-600" /> Upload/Re-upload</span>
                    <span className="ml-auto">Supported: JPG, PNG, WEBP, PDF (max 15MB)</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Lessees;
